import { useEffect } from "react";

// Maps keyboard keys to a movement axis and direction, matching the joystick
// sign conventions used in JoyStickRow:
//   x = lateral strafe (right positive), y = forward (up positive), z = rotation (right positive)
const KEY_TO_AXIS = {
    w: ["y", 1], arrowup: ["y", 1],
    s: ["y", -1], arrowdown: ["y", -1],
    a: ["x", -1], arrowleft: ["x", -1],
    d: ["x", 1], arrowright: ["x", 1],
    q: ["z", -1],
    e: ["z", 1],
};

/** Input types that accept typed characters — exclude range/checkbox/etc. so teleop stays live. */
const TEXT_INPUT_TYPES = new Set([
    "",
    "text",
    "search",
    "email",
    "password",
    "number",
    "url",
    "tel",
    "date",
    "datetime-local",
    "month",
    "time",
    "week",
]);

const isTextLikeInput = (el) => {
    if (!(el instanceof HTMLInputElement)) return false;
    return TEXT_INPUT_TYPES.has((el.type || "").toLowerCase());
};

/** True when keyboard focus is in a text field / editable control (ignore teleop). */
const isEditableElement = (el) => {
    if (!el || !(el instanceof Element)) return false;
    const tag = el.tagName;
    if (tag === "TEXTAREA" || tag === "SELECT") return true;
    if (isTextLikeInput(el)) return true;
    if (el.isContentEditable) return true;
    if (typeof el.closest === "function") {
        const host = el.closest(
            "textarea, select, [contenteditable='true'], [role='textbox']",
        );
        if (host) return true;
        // Only text-like inputs — not range / checkbox / radio / button / file.
        const input = el.closest("input");
        if (input && isTextLikeInput(input)) return true;
    }
    return false;
};

const isTypingContext = (eventTarget) =>
    isEditableElement(eventTarget) || isEditableElement(document.activeElement);

const clamp = (v) => Math.max(-1, Math.min(1, v));

/**
 * Drives the shared movementRef ({ x, y, z }) from WASD / arrow keys (translation)
 * and Q / E (rotation). Values feed the same throttled send loop as the joysticks.
 *
 * @param {React.MutableRefObject<{x:number,y:number,z:number}>} movementRef stable ref shared with JoyStickRow
 * @param {{ enabled?: boolean }} [options]
 */
export default function useKeyboardMovement(movementRef, { enabled = true } = {}) {
    useEffect(() => {
        if (!enabled) return;

        const held = new Set();

        const recompute = () => {
            const m = { x: 0, y: 0, z: 0 };
            for (const k of held) {
                const [axis, val] = KEY_TO_AXIS[k];
                m[axis] += val;
            }
            movementRef.current.x = clamp(m.x);
            movementRef.current.y = clamp(m.y);
            movementRef.current.z = clamp(m.z);
        };

        const releaseAll = () => {
            if (held.size === 0) return;
            held.clear();
            recompute();
        };

        const onKeyDown = (e) => {
            const k = e.key.toLowerCase();
            if (!KEY_TO_AXIS[k]) return;
            // Naming / remarks / any text field must not drive the robot.
            if (isTypingContext(e.target)) {
                releaseAll();
                return;
            }
            e.preventDefault();
            if (e.repeat) return;
            held.add(k);
            recompute();
        };

        const onKeyUp = (e) => {
            const k = e.key.toLowerCase();
            if (!KEY_TO_AXIS[k]) return;
            held.delete(k);
            recompute();
        };

        // Focus moved into an input while WASD was held — stop motion immediately.
        const onFocusIn = (e) => {
            if (isTypingContext(e.target)) releaseAll();
        };

        // If focus/visibility is lost while keys are held, keyup may never fire —
        // zero out so a held key doesn't stay "pressed" (e.g. tab backgrounded by an OS update prompt).
        const onVisibility = () => {
            if (document.hidden) releaseAll();
        };

        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("keyup", onKeyUp);
        window.addEventListener("blur", releaseAll);
        document.addEventListener("focusin", onFocusIn);
        document.addEventListener("visibilitychange", onVisibility);

        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("keyup", onKeyUp);
            window.removeEventListener("blur", releaseAll);
            document.removeEventListener("focusin", onFocusIn);
            document.removeEventListener("visibilitychange", onVisibility);
            releaseAll();
        };
    }, [movementRef, enabled]);
}
